import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import ConsultationPopup from '../../components/feature/ConsultationPopup';

export default function PricingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://static.readdy.ai/image/fa77cf8cc3bbd77c4d454e1d51d6ad11/4b0a760ad430598ce13c316b2b8752fe.png" alt="A2Z Consulting Services" className="h-12 w-auto" />
            <span className="text-xl font-semibold text-[#001F3F]">A2Z Consulting Services</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-[#001F3F] transition-colors hover:text-[#14B8A6]">Home</Link>
            <Link to="/about" className="text-sm font-medium text-[#001F3F] transition-colors hover:text-[#14B8A6]">About</Link>
            <Link to="/services" className="text-sm font-medium text-[#001F3F] transition-colors hover:text-[#14B8A6]">Services</Link>
            <Link to="/pricing" className="text-sm font-medium text-[#14B8A6] transition-colors hover:opacity-80">Pricing</Link>
            <Link to="/resources" className="text-sm font-medium text-[#001F3F] transition-colors hover:text-[#14B8A6]">Resources</Link>
            <Link to="/contact" className="text-sm font-medium text-[#001F3F] transition-colors hover:text-[#14B8A6]">Contact</Link>
          </div>
          <Link to="/contact" className="hidden md:block px-6 py-2.5 bg-[#14B8A6] text-white rounded-full text-sm font-medium transition-all hover:scale-105 hover:bg-[#0D9488] whitespace-nowrap">
            Get Support
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#14B8A6]/10 to-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#14B8A6]/10 rounded-full text-[#14B8A6] text-sm font-medium mb-6">
            <i className="ri-price-tag-3-line"></i>
            <span>Transparent Pricing</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-[#001F3F] mb-6">
            Affordable Support<br />
            <span className="text-[#14B8A6]">For Everyone</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Choose the plan that works best for you. No hidden fees, no long-term commitments. Just honest, transparent pricing for quality support.
          </p>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              {
                name: 'Single Session',
                price: '£45',
                period: 'per session',
                description: 'Perfect for trying out our services or getting help with a specific challenge',
                features: [
                  '60-minute one-on-one session',
                  'Personalized guidance',
                  'Action plan provided',
                  'Email follow-up support',
                  'Flexible scheduling',
                  'No commitment required'
                ],
                color: 'from-[#14B8A6] to-[#0D9488]',
                popular: false
              },
              {
                name: 'Monthly Support',
                price: '£150',
                period: 'per month',
                description: 'Ideal for ongoing support and consistent progress towards your goals',
                features: [
                  '4 sessions per month (60 min each)',
                  'Priority scheduling',
                  'Unlimited email support',
                  'Progress tracking',
                  'Resource materials included',
                  'WhatsApp support',
                  'Monthly goal reviews',
                  'Cancel anytime'
                ],
                color: 'from-[#0D9488] to-[#0F766E]',
                popular: true
              },
              {
                name: 'Intensive Package',
                price: '£400',
                period: '3 months',
                description: 'Comprehensive support for major life transitions or significant goals',
                features: [
                  '12 sessions over 3 months',
                  'Customized support plan',
                  'Priority 24/7 support',
                  'Weekly check-ins',
                  'Comprehensive resources',
                  'Goal achievement guarantee',
                  'Certificate of completion',
                  'Lifetime alumni support'
                ],
                color: 'from-[#0F766E] to-[#115E59]',
                popular: false
              }
            ].map((plan, index) => (
              <div key={index} className={`relative bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden ${plan.popular ? 'ring-4 ring-[#14B8A6] scale-105' : ''}`}>
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-[#14B8A6] text-white px-6 py-2 text-sm font-bold rounded-bl-2xl">
                    Most Popular
                  </div>
                )}
                <div className={`h-2 bg-gradient-to-r ${plan.color}`}></div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-[#001F3F] mb-2">{plan.name}</h3>
                  <p className="text-sm text-gray-600 mb-6 min-h-[48px]">{plan.description}</p>
                  <div className="mb-6">
                    <span className="text-5xl font-bold text-[#001F3F]">{plan.price}</span>
                    <span className="text-gray-500 ml-2">{plan.period}</span>
                  </div>
                  <Link to="/contact" className={`block w-full py-3 rounded-full text-center font-semibold transition-all hover:scale-105 whitespace-nowrap mb-8 ${plan.popular ? 'bg-[#14B8A6] text-white hover:bg-[#0D9488]' : 'bg-gray-100 text-[#001F3F] hover:bg-gray-200'}`}>
                    Get Started
                  </Link>
                  <div className="space-y-4">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl flex-shrink-0 mt-0.5"></i>
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Special Offers */}
          <div className="bg-gradient-to-br from-[#14B8A6]/10 to-white rounded-3xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-[#001F3F] mb-4">Special Offers</h3>
              <p className="text-gray-600">We believe everyone deserves access to quality support</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: 'ri-graduation-cap-line',
                  title: 'Student Discount',
                  discount: '20% Off',
                  description: 'Valid student ID required. Available on all packages.'
                },
                {
                  icon: 'ri-team-line',
                  title: 'Group Sessions',
                  discount: '30% Off',
                  description: 'Bring a friend or study partner. Per person pricing.'
                },
                {
                  icon: 'ri-gift-line',
                  title: 'Referral Bonus',
                  discount: '£25 Credit',
                  description: 'Refer a friend and both receive £25 off your next session.'
                }
              ].map((offer, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-all">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#14B8A6] to-[#0D9488] rounded-xl flex items-center justify-center mb-4">
                    <i className={`${offer.icon} text-2xl text-white`}></i>
                  </div>
                  <h4 className="text-xl font-bold text-[#001F3F] mb-2">{offer.title}</h4>
                  <p className="text-2xl font-bold text-[#14B8A6] mb-3">{offer.discount}</p>
                  <p className="text-sm text-gray-600">{offer.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#001F3F] mb-4">Pricing Questions</h2>
            <p className="text-lg text-gray-600">Everything you need to know about our pricing</p>
          </div>
          <div className="space-y-4">
            {[
              {
                q: 'Can I switch plans at any time?',
                a: 'Yes! You can upgrade, downgrade, or cancel your plan at any time. Changes take effect from your next billing cycle.'
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept all major credit/debit cards, PayPal, and bank transfers. Payment is processed securely through our platform.'
              },
              {
                q: 'Is there a money-back guarantee?',
                a: 'Yes! If you\'re not satisfied with your first session, we offer a full refund within 7 days, no questions asked.'
              },
              {
                q: 'Do you offer payment plans?',
                a: 'Yes, we offer flexible payment plans for our 3-month intensive package. Contact us to discuss options that work for your budget.'
              },
              {
                q: 'What happens if I miss a session?',
                a: 'We require 24 hours notice for cancellations. With proper notice, you can reschedule at no charge. Late cancellations may forfeit the session.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-all">
                <h4 className="text-lg font-bold text-[#001F3F] mb-3 flex items-start gap-3">
                  <i className="ri-question-line text-[#14B8A6] text-xl flex-shrink-0 mt-1"></i>
                  {item.q}
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed pl-8">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#14B8A6] to-[#0D9488]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Book your first session today and take the first step towards achieving your goals.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button onClick={() => setIsPopupOpen(true)} className="inline-flex items-center gap-2 px-10 py-4 bg-white text-[#14B8A6] rounded-full font-bold text-base transition-all hover:scale-105 hover:shadow-2xl whitespace-nowrap cursor-pointer">
              Book Free Consultation <i className="ri-calendar-line"></i>
            </button>
            <a href="https://wa.me/447494699771" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-10 py-4 border-2 border-white text-white rounded-full font-bold text-base transition-all hover:scale-105 hover:bg-white hover:text-[#14B8A6] whitespace-nowrap">
              <i className="ri-whatsapp-line text-xl"></i> Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] rounded-t-3xl pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="https://static.readdy.ai/image/fa77cf8cc3bbd77c4d454e1d51d6ad11/4b0a760ad430598ce13c316b2b8752fe.png" alt="A2Z" className="h-10 w-auto" />
                <span className="text-xl font-semibold text-white">A2Z Consulting Services</span>
              </div>
              <p className="text-sm text-gray-400 mb-8">Empowering every step of your journey</p>
              <h4 className="text-lg font-bold text-white mb-3">Stay Updated</h4>
              <p className="text-sm text-gray-400 mb-4">Get the latest insights and news</p>
              <div className="flex flex-col gap-3">
                <input type="email" placeholder="Enter your email" className="w-full px-4 py-3 bg-[#2A2A2A] text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-white/20" />
                <button className="px-7 py-3 bg-white text-black rounded-full text-sm font-medium transition-all hover:scale-105 whitespace-nowrap">
                  Subscribe <i className="ri-arrow-right-line ml-1"></i>
                </button>
              </div>
            </div>
            <div>
              <h4 className="text-base font-semibold text-white mb-6">Quick Links</h4>
              <div className="flex flex-col gap-4">
                <Link to="/" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Home</Link>
                <Link to="/about" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">About Us</Link>
                <Link to="/services" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Services</Link>
                <Link to="/team" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Our Team</Link>
                <Link to="/case-studies" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Success Stories</Link>
                <Link to="/blog" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Resources</Link>
                <Link to="/pricing" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Pricing</Link>
                <Link to="/faq" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">FAQ</Link>
                <Link to="/contact" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-base font-semibold text-white mb-6">Our Services</h4>
              <div className="flex flex-col gap-4">
                <Link to="/services" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Academic Support</Link>
                <Link to="/services" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Career Guidance</Link>
                <Link to="/services" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Personal Development</Link>
                <Link to="/services" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Startup Mentorship</Link>
              </div>
            </div>
            <div>
              <h4 className="text-base font-semibold text-white mb-6">Contact</h4>
              <div className="flex flex-col gap-4 mb-8">
                <a href="tel:+447494699771" className="text-base text-white hover:underline">+44 7494 699 771</a>
                <a href="mailto:hello@a2zconsultingservices.com" className="text-base text-white hover:underline">hello@a2zconsultingservices.com</a>
                <a href="https://wa.me/447494699771" target="_blank" rel="noopener noreferrer" className="text-base text-white hover:underline flex items-center gap-2">
                  <i className="ri-whatsapp-line"></i> WhatsApp Us
                </a>
              </div>
              <h5 className="text-sm text-gray-400 mb-4">Follow Us</h5>
              <p className="text-sm text-gray-400 leading-relaxed">123 Business Avenue<br />Manchester, M1 1AA, UK</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">LinkedIn</a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Twitter</a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Facebook</a>
            </div>
            <p className="text-sm text-gray-400 text-center">© 2025 A2Z Consulting Services. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <Link to="/privacy-policy" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>

      <ConsultationPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
}
